import { useEffect, useState, useRef } from "react";
import { api, formatApiError, fmtNGN, img, tierStyle } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Gavel, Users, Clock, Sparkles, CalendarClock, Lock, Trophy, Tag, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

function timeLeft(target) {
  const ms = new Date(target) - new Date();
  if (ms <= 0) return "Closing…";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// mm:ss countdown for the live window — hard-capped at 5 minutes so it can never run away.
function countdown(target) {
  let ms = Math.max(0, new Date(target) - new Date());
  ms = Math.min(ms, 5 * 60000);
  const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Live top bid climbs from start price -> projected top bid across the auction window
function liveBid(a) {
  const start = a.start_price ?? a.current_bid ?? 0;
  const top = a.max_price ?? a.current_bid ?? start;
  const t0 = new Date(a.created_at || a.list_at || Date.now()).getTime();
  const t1 = new Date(a.ends_at).getTime();
  const now = Date.now();
  if (now >= t1) return a.current_bid ?? top;
  const p = t1 > t0 ? Math.min(1, Math.max(0, (now - t0) / (t1 - t0))) : 0;
  // ease-out so early bids jump then slow near the top
  const eased = 1 - Math.pow(1 - p, 2);
  return Math.round(start + (top - start) * eased);
}

// Friendly label for the next auction window, e.g. "Wednesday, 10:00".
function nextAuctionLabel(config) {
  const day = config?.auction_day_label || "Wednesday";
  try {
    const t = new Date(config.next_window).toLocaleString("en-NG", { hour: "2-digit", minute: "2-digit" });
    return `${day} ${t}`;
  } catch { return day; }
}

export default function Auctions() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [config, setConfig] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [sales, setSales] = useState([]);
  const [sellOpen, setSellOpen] = useState(false);
  const [listing, setListing] = useState(false);
  const [listedIds, setListedIds] = useState(new Set());
  const [salesPage, setSalesPage] = useState(1);
  const [tick, setTick] = useState(0);
  const endedSeen = useRef(new Set());

  const load = () => {
    api.get("/auctions").then((r) => setItems(r.data.items));
    api.get("/auctions/config").then((r) => setConfig(r.data));
    api.get("/holdings").then((r) => setHoldings(r.data.items.filter((h) => h.status === "owned")));
    api.get("/auctions/recent-sales").then((r) => setSales(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);

  // When one of MY live auctions hits 0, announce success and refresh.
  useEffect(() => {
    if (!items || !user) return;
    items.forEach((a) => {
      if (a.status === "live" && a.seller_id === user.id && new Date(a.ends_at) <= new Date() && !endedSeen.current.has(a.id)) {
        endedSeen.current.add(a.id);
        toast.success("Your auction was successful, kindly check your wallets");
        setTimeout(load, 1500);
      }
    });
  }, [tick, items, user]);

  const openSell = async () => {
    if (config && config.session_used) {
      toast.info(`You've already used your auction session. Come back on the next auction day (${nextAuctionLabel(config)}) to list again.`);
      return;
    }
    if (config && !config.eligible) {
      toast.warning(config.reasons.join(" "));
      setSellOpen(true);
      return;
    }
    if (config && !config.window_open) {
      toast.info(`Auctions run on Wednesdays. Next window: ${new Date(config.next_window).toLocaleString("en-NG", { weekday: "long", hour: "2-digit", minute: "2-digit" })}.`);
      setSellOpen(true);
      return;
    }
    try {
      await api.post("/auctions/session/start");
      setSellOpen(true);
    } catch (e) {
      toast.info(formatApiError(e));
      load();
    }
  };

  const endSession = async () => {
    try { await api.post("/auctions/session/end"); } catch { /* ignore */ }
    load();
    setListedIds(new Set());
  };

  const listArtwork = async (holdingId) => {
    setListing(true);
    try {
      await api.post("/auctions/list", { holding_id: holdingId });
      toast.success("Artwork listed. You can list more in this session or tap Done when finished.");
      setListedIds((prev) => new Set(prev).add(holdingId));
      // Refresh holdings so the listed piece drops off, but keep the session (dialog) open.
      api.get("/holdings").then((r) => setHoldings(r.data.items.filter((h) => h.status === "owned")));
      api.get("/auctions").then((r) => setItems(r.data.items));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setListing(false); }
  };

  if (!items || !config) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const live = items.filter((a) => a.status === "live");

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold">Wednesday Auctions</h1>
          <p className="text-sm text-muted-foreground mt-1">List your Artworks for auction sales here</p>
        </div>
        <Button className="rounded-full h-11 px-6 navy-gradient text-white font-semibold disabled:opacity-60" onClick={openSell} disabled={config.session_used} data-testid="sell-artwork-btn">
          <Tag className="h-4 w-4 mr-2" /> {config.session_used ? "Session Used" : "Sell an Artwork"}
        </Button>
      </div>

      {/* Session-used banner: makes the one-listing-per-auction-day lock feel intentional */}
      {config.session_used && (
        <div className="rounded-3xl p-6 border bg-emerald-500/10 border-emerald-500/30" data-testid="auction-session-used-banner">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl grid place-items-center shrink-0 bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <p className="font-heading text-lg font-bold">You've used your auction session today</p>
              <p className="text-sm text-muted-foreground mt-1">
                You've already listed an artwork in this auction. To keep things fair, each member gets one listing per auction day.
                You can buy and list again on the next auction day — <span className="font-semibold text-foreground">{nextAuctionLabel(config)}</span>
                {new Date(config.next_window) > new Date() ? <> (opens in {timeLeft(config.next_window)}).</> : "."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Window + eligibility banner */}
      <div className={`rounded-3xl p-6 border ${config.window_open ? "bg-gold/10 border-gold/30" : "bg-card border-border"}`} data-testid="auction-window-banner">
        <div className="flex items-start gap-4">
          <div className={`h-12 w-12 rounded-2xl grid place-items-center shrink-0 ${config.window_open ? "navy-gradient" : "bg-secondary"}`}>
            {config.window_open ? <Gavel className="h-6 w-6 text-white" /> : <CalendarClock className="h-6 w-6" />}
          </div>
          <div className="flex-1">
            {config.window_open ? (
              <>
                <p className="font-heading text-lg font-bold text-gold">Auction is LIVE {config.is_test_mode ? "(Test Mode)" : "now"}</p>
                <p className="text-sm text-muted-foreground">Open Wednesdays 10:00–17:30. List an eligible artwork to join.</p>
              </>
            ) : (
              <>
                <p className="font-heading text-lg font-bold">Auction opens Wednesday</p>
                <p className="text-sm text-muted-foreground">Next window: {new Date(config.next_window).toLocaleString("en-NG", { weekday: "long", hour: "2-digit", minute: "2-digit" })} • Opens in {timeLeft(config.next_window)}</p>
              </>
            )}
            {!config.eligible && (
              <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
                <Lock className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{config.reasons.join(" ")}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live auctions */}
      <div>
        <h3 className="font-heading text-xl font-bold mb-3">Live Auctions {live.length > 0 && <span className="text-gold">({live.length})</span>}</h3>
        {live.length === 0 ? (
          <div className="bg-card border border-border rounded-3xl p-10 text-center text-sm text-muted-foreground">No live auctions right now. Check back on Wednesday or list one of your holdings.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {live.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => nav(`/auctions/${a.id}`)} data-testid={`auction-card-${a.id}`}
                className="bg-card border border-border rounded-3xl overflow-hidden cursor-pointer group hover:shadow-xl transition-shadow">
                <div className="aspect-[3/4] overflow-hidden bg-muted relative">
                  <img src={img(a.thumbnail || a.image)} alt={a.title} loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute top-3 left-3 text-xs font-bold tabular-nums bg-black/70 text-white px-2.5 py-1 rounded-full flex items-center gap-1.5" data-testid={`auction-countdown-${a.id}`}><Clock className="h-3.5 w-3.5 text-gold" /> {countdown(a.ends_at)}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold truncate">{a.title}</h3>
                  <p className="text-xs text-muted-foreground truncate">by {a.seller_username}</p>
                  <div className="flex items-end justify-between mt-3">
                    <div><p className="text-xs text-muted-foreground">Top bid</p><p className="font-heading text-2xl font-bold text-gold tabular-nums transition-all" data-testid={`auction-bid-${a.id}`}>{fmtNGN(liveBid(a))}</p></div>
                    <div className="text-right text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> {a.bid_count} bids</div>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1"><Sparkles className="h-3 w-3 text-gold" /> Top bid target ₦{a.max_price?.toLocaleString()}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Recent sales */}
      {sales.length > 0 && (
        <div>
          <h3 className="font-heading text-xl font-bold mb-3">Recent Sales</h3>
          <div className="space-y-2" data-testid="recent-sales-list">
            {sales.slice((salesPage - 1) * 10, salesPage * 10).map((a) => (
              <div key={a.id} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4" data-testid={`recent-sale-${a.id}`}>
                <img src={img(a.image)} alt={a.title} loading="lazy" className="h-14 w-14 rounded-xl object-cover bg-muted" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-heading font-semibold truncate">{a.title}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${tierStyle(a.tier)}`}>{a.tier}</span>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Trophy className="h-3 w-3 text-gold shrink-0" /> Sold to {a.buyer}</p>
                </div>
                <span className="font-bold text-gold shrink-0">{fmtNGN(a.price)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-3 pt-3" data-testid="recent-sales-pagination">
            <Button variant="outline" className="rounded-full" disabled={salesPage <= 1} onClick={() => setSalesPage((p) => p - 1)} data-testid="sales-prev">Previous</Button>
            <span className="text-sm text-muted-foreground">Page {salesPage} of {Math.max(1, Math.ceil(sales.length / 10))}</span>
            <Button variant="outline" className="rounded-full" disabled={salesPage >= Math.ceil(sales.length / 10)} onClick={() => setSalesPage((p) => p + 1)} data-testid="sales-next">Next</Button>
          </div>
        </div>
      )}

      {/* Sell dialog */}
      <Dialog open={sellOpen} onOpenChange={(o) => { setSellOpen(o); if (!o) endSession(); }}>
        <DialogContent className="rounded-3xl sm:max-w-2xl max-h-[90vh] overflow-y-auto no-scrollbar">
          <DialogHeader><DialogTitle className="font-heading">List artworks for auction</DialogTitle></DialogHeader>
          {config && !config.eligible && (
            <div className="text-sm bg-gold/10 border border-gold/30 rounded-2xl p-3 flex items-start gap-2" data-testid="sell-eligibility-warning">
              <Lock className="h-4 w-4 mt-0.5 shrink-0 text-gold" />
              <span>{config.reasons.join(" ")}</span>
            </div>
          )}
          <p className="text-sm text-muted-foreground">This is your one auction session for today — list as many of your holdings as you like now. Once you tap <span className="font-semibold text-foreground">Done</span>, you can't start another session until the next auction day.</p>
          {holdings.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground">{listedIds.size > 0 ? "All your holdings are listed. Tap Done to finish your session." : "You have no artworks available to list."}</p>
              {listedIds.size === 0 && <Button className="rounded-full mt-4 navy-gradient text-white font-semibold" onClick={() => nav("/gallery")}>Browse Gallery</Button>}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {holdings.map((h) => (
                <div key={h.id} className="bg-secondary rounded-2xl p-2.5 flex items-center gap-2.5" data-testid={`sell-holding-${h.id}`}>
                  <img src={img(h.thumbnail || h.image)} alt={h.title} className="h-11 w-11 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{h.title}</p>
                    <p className="text-xs text-muted-foreground">Bought {fmtNGN(h.purchase_price_ngn)}</p>
                  </div>
                  <Button size="sm" className="rounded-full navy-gradient text-white font-semibold disabled:opacity-50 shrink-0" disabled={listing || listedIds.has(h.id)} onClick={() => listArtwork(h.id)} data-testid={`list-${h.id}`}>
                    {listedIds.has(h.id) ? "Listed ✓" : (listing ? <Loader2 className="h-4 w-4 animate-spin" /> : "List")}
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button onClick={() => setSellOpen(false)} data-testid="sell-done-btn"
            className="w-full h-11 rounded-full mt-2 navy-gradient text-white font-semibold">
            {listedIds.size > 0 ? `Done — ${listedIds.size} listed` : "Done"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
